/**
 * BroadcastChannel 认证同步
 *
 * 用于跨标签页实时同步登录状态
 */
import type { User } from '@/types';

/**
 * 认证消息类型
 */
export type AuthMessage =
  | { type: 'LOGIN'; token: string; user: User }
  | { type: 'LOGOUT' }
  | { type: 'PING' }
  | { type: 'PONG'; user: User | null };

/**
 * 消息回调函数类型
 */
export type AuthMessageHandler = (message: AuthMessage) => void;

/**
 * BroadcastChannel 认证管理器
 */
export class AuthBroadcast {
  private channel: BroadcastChannel | null = null;
  private handlers: Set<AuthMessageHandler> = new Set();
  private readonly channelName = 'jointo-auth';

  constructor() {
    this.init();
  }

  /**
   * 初始化 BroadcastChannel
   */
  private init() {
    // 检查浏览器支持
    if (typeof BroadcastChannel === 'undefined') {
      console.warn('⚠️ BroadcastChannel 不支持，跨标签页同步将降级到 localStorage 事件');
      return;
    }

    try {
      this.channel = new BroadcastChannel(this.channelName);
      this.channel.onmessage = (event) => {
        this.handleMessage(event.data);
      };
      console.log('✅ BroadcastChannel 已初始化');
    } catch (error) {
      console.error('❌ BroadcastChannel 初始化失败:', error);
    }
  }

  /**
   * 处理接收到的消息
   */
  private handleMessage(message: AuthMessage) {
    console.log('📨 收到 BroadcastChannel 消息:', message.type);
    this.handlers.forEach((handler) => {
      try {
        handler(message);
      } catch (error) {
        console.error('❌ 消息处理器执行失败:', error);
      }
    });
  }

  /**
   * 发送消息
   */
  private postMessage(message: AuthMessage) {
    if (!this.channel) {
      console.warn('⚠️ BroadcastChannel 不可用，无法发送消息');
      return;
    }

    try {
      this.channel.postMessage(message);
      console.log('📤 发送 BroadcastChannel 消息:', message.type);
    } catch (error) {
      console.error('❌ 发送消息失败:', error);
    }
  }

  /**
   * 广播登录事件
   */
  broadcastLogin(token: string, user: User) {
    this.postMessage({ type: 'LOGIN', token, user });
  }

  /**
   * 广播退出登录事件
   */
  broadcastLogout() {
    this.postMessage({ type: 'LOGOUT' });
  }

  /**
   * 发送 PING（检测其他标签页是否已登录）
   */
  sendPing() {
    this.postMessage({ type: 'PING' });
  }

  /**
   * 发送 PONG（响应 PING，告知当前登录状态）
   */
  sendPong(user: User | null) {
    this.postMessage({ type: 'PONG', user });
  }

  /**
   * 注册消息监听器
   */
  onMessage(handler: AuthMessageHandler) {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  /**
   * 检查是否支持 BroadcastChannel
   */
  isSupported(): boolean {
    return this.channel !== null;
  }

  /**
   * 关闭 BroadcastChannel
   */
  close() {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
      console.log('🔒 BroadcastChannel 已关闭');
    }
    this.handlers.clear();
  }
}

/**
 * 全局单例
 */
let authBroadcastInstance: AuthBroadcast | null = null;

/**
 * 获取 AuthBroadcast 单例
 */
export function getAuthBroadcast(): AuthBroadcast {
  if (!authBroadcastInstance) {
    authBroadcastInstance = new AuthBroadcast();
  }
  return authBroadcastInstance;
}
